import { useCallback, useEffect, useMemo, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi } from "../../api/shopMode";
import type {
  ConfirmDeliveryLinePayload,
  DeliveryLineFoundBy,
  ScheduledDelivery,
  ScheduledDeliveryLine,
} from "../../api/types";
import { linesWithFoundFirst } from "../../utils/findDeliveryLine";
import { differencePayload } from "../../utils/deliveryDifference";
import { CaptureLineBarcode } from "../deliveries/CaptureLineBarcode";
import { itemStickerPrinter, printableBarcode, printItemSticker } from "./printItemSticker";
import { itemStickerOutcomeText } from "./itemStickerOutcome";
import { ShopFloorItemBarcodePrint } from "./ShopFloorItemBarcodePrint";
import { DeliveryScanBox } from "../deliveries/DeliveryScanBox";
import { ShopFloorIcon } from "./ShopFloorIcon";
import {
  ShopFloorDeliveryLinePhoto,
  type DeliveryLinePhotoItem,
} from "./ShopFloorDeliveryLinePhoto";
import { usePhotoCapture } from "../usePhotoCapture";
import { uomLabel } from "./shopFloorFormat";
import { useT } from "../../i18n/LanguageProvider";
import { type TranslateFn } from "../../i18n/t";
import { dateTimeText } from "../../i18n/intl";

interface Props {
  deliveryId: string;
  onBack: () => void;
  onChanged: () => Promise<void>;
}

const qty = (value: string | null) =>
  value === null ? "—" : Number(value).toString();

/** A server decimal string that is a real amount above zero. */
const positive = (value: string | null | undefined) =>
  value != null && Number(value) > 0;

/**
 * `record` without `key` — the same object back when the key is absent, so
 * React skips the render. (Split over lines on purpose: the i18n gate reads
 * `<T>(record: Record<` on one line as a JSX text node.)
 */
function withoutKey<T>(
  record: Record<string, T>,
  key: string,
): Record<string, T> {
  if (!(key in record)) return record;
  const next = { ...record };
  delete next[key];
  return next;
}

function lineStatus(t: TranslateFn, line: ScheduledDeliveryLine) {
  const status = line.status;
  if (status === "CONFIRMED_AS_EXPECTED") {
    return { label: t("receiving.statusExact"), className: "badge success" };
  }
  if (status === "CONFIRMED_WITH_DIFFERENCE") {
    // Owner, 2026-10-01: the badge says HOW MANY were different — missing,
    // damaged or extra — so the row answers it without reading the line below.
    const differing = [line.shortageQty, line.damagedQty, line.overageQty]
      .map((value) => (positive(value) ? Number(value) : 0))
      .reduce((sum, value) => sum + value, 0);
    const count = Number(differing.toFixed(3));
    return {
      label:
        count > 0
          ? t("receiving.statusDifferenceQty", {
              qty: String(count),
              unit: uomLabel(line.item.defaultUom, count),
            })
          : t("receiving.statusDifference"),
      className: "badge primary",
    };
  }
  if (status === "MISSING") {
    return { label: t("receiving.statusMissing"), className: "badge danger" };
  }
  return { label: t("receiving.statusPending"), className: "badge muted" };
}

/** The delivery's own state, in words rather than as a raw enum. */
function deliveryStatusWords(t: TranslateFn, status: ScheduledDelivery["status"]) {
  if (status === "COMPLETED") return t("receiving.deliveryCompleted");
  if (status === "CANCELED") return t("receiving.deliveryCanceled");
  if (status === "IN_PROGRESS") return t("receiving.deliveryInProgress");
  return t("receiving.deliveryScheduled");
}

/**
 * G-121 S2: the panel's one capture flow serves two kinds of photo. An ITEM
 * photo goes through background removal and up to the catalogue on its own
 * (G-116). A DAMAGE photo is evidence of what arrived broken: the original,
 * no cut-out, held on the row until the line's confirmation carries it up.
 */
type PhotoTarget =
  | { kind: "item"; item: DeliveryLinePhotoItem }
  | { kind: "damage"; lineId: string };

export function ShopFloorDeliveryReceivingPanel({
  deliveryId,
  onBack,
  onChanged,
}: Props) {
  const { t } = useT();
  const [delivery, setDelivery] = useState<ScheduledDelivery | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDelivery(await shopModeApi.floorGetDelivery(deliveryId));
    } catch (err: unknown) {
      // Call-time, so a language switch does not re-run `load`.
      setError(errorText(err, "receiving.couldNotLoad"));
    } finally {
      setLoading(false);
    }
  }, [deliveryId]);

  useEffect(() => {
    void load();
  }, [load]);

  const confirmedCount = useMemo(
    () => delivery?.lines.filter((line) => line.status !== "PENDING").length ?? 0,
    [delivery],
  );

  // G-59: the line a scan or a typed code found — first in the list,
  // highlighted, and its confirmation records how it was found.
  const [found, setFound] = useState<{ lineId: string; how: DeliveryLineFoundBy } | null>(null);
  const [unmatchedCode, setUnmatchedCode] = useState<string | null>(null);
  const orderedLines = useMemo(
    () => (delivery ? linesWithFoundFirst(delivery.lines, found?.lineId ?? null) : []),
    [delivery, found],
  );

  // G-121 S2: a damage photo waits on its row, keyed by line, until that
  // line is confirmed — it is sent WITH the confirmation, never on its own.
  const [damagePhotos, setDamagePhotos] = useState<Record<string, File>>({});
  const setDamagePhoto = (lineId: string, file: File | null) =>
    setDamagePhotos((prev) =>
      file ? { ...prev, [lineId]: file } : withoutKey(prev, lineId),
    );

  // The item is shared by every row that carries it (D-67: one item, two
  // zones), so a change to the item lands on all of them — its photo (G-116),
  // its issued code (G-130).
  const patchLinesItem = (
    itemId: string,
    patch: Partial<ScheduledDeliveryLine["item"]>,
  ) =>
    setDelivery((current) =>
      current
        ? {
            ...current,
            lines: current.lines.map((line) =>
              line.item.id === itemId
                ? { ...line, item: { ...line.item, ...patch } }
                : line,
            ),
          }
        : current,
    );

  /**
   * G-130 (owner 2026-10-03): "Print barcode" on every receiving row — the
   * item card's one-tap sticker (G-100), printed from the door. An item with
   * no barcode first gets the shop's own code (G-99) in the same tap, then
   * the sticker; one sticker per tap. Without a paired printer the pairing
   * panel opens under the row instead (the card's fallback, reused).
   *
   * ONE print at a time: while a line prints, every print square waits
   * (`printLocked`), so a tap on another row is refused visibly rather than
   * dropped by the guard below.
   */
  const [printBusyLineId, setPrintBusyLineId] = useState<string | null>(null);
  const [printWords, setPrintWords] = useState<
    Record<string, { notice: string | null; error: string | null }>
  >({});
  const [printFallback, setPrintFallback] = useState<Record<string, string>>({});
  const closePrintFallback = (lineId: string) =>
    setPrintFallback((prev) => withoutKey(prev, lineId));
  /** A row's print words and fallback go when it prints again or the line moves on. */
  const forgetPrint = (lineId: string) => {
    setPrintWords((prev) => withoutKey(prev, lineId));
    closePrintFallback(lineId);
  };
  const printLineSticker = async (line: ScheduledDeliveryLine) => {
    if (printBusyLineId) return;
    setPrintBusyLineId(line.id);
    forgetPrint(line.id);
    try {
      let barcode = printableBarcode(line.item);
      if (!barcode) {
        // The code is committed before any paper moves (G-99) and lands on
        // every row of the item, so a print that then fails is one more tap,
        // not a second code. Its failure gets its own words: nothing has
        // reached a printer yet.
        const issued = await shopModeApi
          .floorIssueItemBarcode(line.item.id)
          .catch((err: unknown) => {
            setPrintWords((prev) => ({
              ...prev,
              [line.id]: { notice: null, error: errorText(err, "itemCard.didNotGoThrough") },
            }));
            return null;
          });
        if (!issued) return;
        patchLinesItem(line.item.id, {
          issuedBarcode: { code: issued.code, barcodeText: issued.barcodeText },
        });
        barcode = issued.barcodeText;
      }
      // A const: the closures below see a string, not a `let` that may move.
      const sticker = { itemName: line.item.name, barcode };
      const printer = itemStickerPrinter();
      if (!printer) {
        setPrintFallback((prev) => ({ ...prev, [line.id]: sticker.barcode }));
        return;
      }
      const result = await printItemSticker(printer, sticker);
      setPrintWords((prev) => ({ ...prev, [line.id]: itemStickerOutcomeText(result, printer) }));
    } catch (err: unknown) {
      setPrintWords((prev) => ({
        ...prev,
        [line.id]: { notice: null, error: errorText(err, "itemSticker.couldNotPrint") },
      }));
    } finally {
      setPrintBusyLineId(null);
    }
  };

  const confirm = async (
    lineId: string,
    payload: ConfirmDeliveryLinePayload,
    photo: File | null = null,
  ) => {
    const foundBy: DeliveryLineFoundBy =
      found?.lineId === lineId ? found.how : "TAPPED";
    const updated = await shopModeApi.floorConfirmDeliveryLine(
      deliveryId,
      lineId,
      { ...payload, foundBy },
      photo,
    );
    setDelivery(updated);
    setDamagePhoto(lineId, null);
    forgetPrint(lineId);
    if (found?.lineId === lineId) setFound(null);
    await onChanged();
  };

  const captureBarcode = async (lineId: string, barcode: string) => {
    const updated = await shopModeApi.floorCaptureDeliveryLineBarcode(
      deliveryId,
      lineId,
      { barcode },
    );
    setDelivery(updated);
  };

  // G-116: ONE capture flow for the whole panel (two hidden inputs, one
  // review dialog), aimed at whichever row asked. A row's errors are kept by
  // item, on their own line, never on the row's confirm error.
  const [photoTarget, setPhotoTarget] = useState<PhotoTarget | null>(null);
  const [photoSavingItemId, setPhotoSavingItemId] = useState<string | null>(null);
  const [photoErrors, setPhotoErrors] = useState<Record<string, string>>({});
  const clearPhotoError = (itemId: string) =>
    setPhotoErrors((prev) => withoutKey(prev, itemId));
  const savePhoto = async (item: DeliveryLinePhotoItem, file: File) => {
    setPhotoSavingItemId(item.id);
    clearPhotoError(item.id);
    try {
      const updated = await shopModeApi.floorReplaceItemPhoto(item.id, file);
      patchLinesItem(item.id, { imageKey: updated.imageKey });
    } catch (err: unknown) {
      setPhotoErrors((prev) => ({
        ...prev,
        [item.id]: errorText(err, "receiving.couldNotSavePhoto"),
      }));
    } finally {
      setPhotoSavingItemId(null);
    }
  };
  const photoCapture = usePhotoCapture({
    onCommit: (file) => {
      if (!photoTarget) return;
      if (photoTarget.kind === "item") void savePhoto(photoTarget.item, file);
      else setDamagePhoto(photoTarget.lineId, file);
    },
    removeBackground: shopModeApi.floorRemoveBackground,
    title: photoTarget?.kind === "item" ? photoTarget.item.name : "",
    // A damage photo is evidence: the original goes, with no review dialog.
    autoRemoveBackground: photoTarget?.kind !== "damage",
  });
  const openPhotoCamera = (item: DeliveryLinePhotoItem) => {
    setPhotoTarget({ kind: "item", item });
    photoCapture.openCamera();
  };
  const openDamageCamera = (lineId: string) => {
    setPhotoTarget({ kind: "damage", lineId });
    photoCapture.openCamera();
  };

  return (
    <div className="card shop-floor-delivery-receiving">
      <div className="shop-floor-delivery-receiving__header">
        <button type="button" className="button plain" onClick={onBack}>
          <ShopFloorIcon name="arrow-left" size={20} />
          {t("receiving.backToTasks")}
        </button>
        {delivery ? (
          <span
            className={
              delivery.status === "COMPLETED"
                ? "badge success"
                : delivery.status === "CANCELED"
                  ? "badge danger"
                  : delivery.status === "IN_PROGRESS"
                    ? "badge primary"
                    : "badge muted"
            }
          >
            {deliveryStatusWords(t, delivery.status)}
          </span>
        ) : null}
      </div>

      {loading ? <p className="muted">{t("receiving.loadingDelivery")}</p> : null}
      {error ? (
        <div className="alert error">
          {error}
          <button type="button" className="button plain" onClick={() => void load()}>
            {t("receiving.tryAgain")}
          </button>
        </div>
      ) : null}

      {delivery ? (
        <>
          <div className="shop-floor-delivery-receiving__title">
            <span className="shop-floor-delivery-receiving__icon">
              <ShopFloorIcon name="delivery" size={27} />
            </span>
            <div>
              <h3>
                {t("receiving.receiveFrom", { supplier: delivery.supplier.name })}
              </h3>
              <p className="muted">
                {t("receiving.expectedSummary", {
                  when: dateTimeText(new Date(delivery.scheduledFor)),
                  done: confirmedCount,
                  total: delivery.lines.length,
                })}
              </p>
            </div>
          </div>

          <div
            className="shop-floor-delivery-progress"
            role="progressbar"
            aria-label={t("receiving.progressLabel")}
            aria-valuemin={0}
            aria-valuemax={delivery.lines.length}
            aria-valuenow={confirmedCount}
          >
            <span
              style={{
                width: `${delivery.lines.length ? (confirmedCount / delivery.lines.length) * 100 : 0}%`,
              }}
            />
          </div>

          {delivery.notes ? (
            <div className="shop-floor-delivery-note">
              <ShopFloorIcon name="notes" size={20} />
              <span>{delivery.notes}</span>
            </div>
          ) : null}

          {delivery.status === "COMPLETED" ? (
            <div className="alert success">{t("receiving.allReceived")}</div>
          ) : null}

          {delivery.status !== "COMPLETED" && delivery.status !== "CANCELED" ? (
            <DeliveryScanBox
              delivery={delivery}
              onFound={(line, how) => setFound({ lineId: line.id, how })}
              onUnmatched={setUnmatchedCode}
            />
          ) : null}

          <div className="shop-floor-delivery-lines">
            {orderedLines.map((line) => (
              <ShopFloorDeliveryLine
                key={line.id}
                line={line}
                highlighted={found?.lineId === line.id}
                unmatchedCode={unmatchedCode}
                disabled={
                  delivery.status === "COMPLETED" || delivery.status === "CANCELED"
                }
                onConfirm={(payload, photo) => confirm(line.id, payload, photo)}
                onCaptureBarcode={async (barcode) => {
                  await captureBarcode(line.id, barcode);
                  setUnmatchedCode(null);
                }}
                photoSaving={photoSavingItemId === line.item.id}
                photoError={photoErrors[line.item.id] ?? null}
                onOpenCamera={openPhotoCamera}
                onClearPhotoError={clearPhotoError}
                damagePhoto={damagePhotos[line.id] ?? null}
                onOpenDamageCamera={() => openDamageCamera(line.id)}
                onRemoveDamagePhoto={() => setDamagePhoto(line.id, null)}
                onPrint={() => void printLineSticker(line)}
                printBusy={printBusyLineId === line.id}
                printLocked={printBusyLineId !== null}
                printWords={printWords[line.id] ?? null}
                printFallback={printFallback[line.id] ?? null}
                onClosePrintFallback={() => closePrintFallback(line.id)}
              />
            ))}
          </div>
          {photoCapture.ui}
        </>
      ) : null}
    </div>
  );
}

function ShopFloorDeliveryLine({
  line,
  disabled,
  highlighted,
  unmatchedCode,
  onConfirm,
  onCaptureBarcode,
  photoSaving,
  photoError,
  onOpenCamera,
  onClearPhotoError,
  damagePhoto,
  onOpenDamageCamera,
  onRemoveDamagePhoto,
  onPrint,
  printBusy,
  printLocked,
  printWords,
  printFallback,
  onClosePrintFallback,
}: {
  line: ScheduledDeliveryLine;
  disabled: boolean;
  highlighted: boolean;
  unmatchedCode: string | null;
  onConfirm: (payload: ConfirmDeliveryLinePayload, photo?: File | null) => Promise<void>;
  onCaptureBarcode: (barcode: string) => Promise<void>;
  photoSaving: boolean;
  photoError: string | null;
  onOpenCamera: (item: DeliveryLinePhotoItem) => void;
  onClearPhotoError: (itemId: string) => void;
  /** G-121 S2: the damage photo waiting on this row, if one was taken. */
  damagePhoto: File | null;
  onOpenDamageCamera: () => void;
  onRemoveDamagePhoto: () => void;
  /** G-130: print this item's barcode sticker (issuing the shop's code first when it has none). */
  onPrint: () => void;
  /** This row's own print is under way — the label says so. */
  printBusy: boolean;
  /** ANY row is printing: one sticker at a time, so every print square waits. */
  printLocked: boolean;
  printWords: { notice: string | null; error: string | null } | null;
  /** The barcode to print through the pairing panel, when no printer is paired. */
  printFallback: string | null;
  onClosePrintFallback: () => void;
}) {
  const [differenceOpen, setDifferenceOpen] = useState(false);
  // Owner, 2026-10-01: the expected quantity is known, so the form asks only
  // what is different — how many — and whether those arrived damaged rather
  // than not at all.
  const [differenceQty, setDifferenceQty] = useState("");
  const [damaged, setDamaged] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState<"exact" | "difference" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useT();
  const status = lineStatus(t, line);
  const pending = line.status === "PENDING" && !disabled;

  const submitExact = async () => {
    setBusy("exact");
    setError(null);
    try {
      await onConfirm({ asExpected: true });
    } catch (err: unknown) {
      setError(errorText(err, "receiving.couldNotConfirm"));
    } finally {
      setBusy(null);
    }
  };

  const submitDifference = async () => {
    const result = differencePayload({
      expectedQty: line.expectedQty,
      count: differenceQty,
      damaged,
      hasPhoto: damagePhoto !== null,
    });
    if (!result.ok) {
      setError(
        result.problem === "count"
          ? t("receiving.missingRequired")
          : result.problem === "tooMany"
            ? t("receiving.missingTooMany", { qty: qty(line.expectedQty) })
            : t("receiving.damagePhotoRequired"),
      );
      return;
    }
    setBusy("difference");
    setError(null);
    try {
      await onConfirm(
        {
          ...result.payload,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        damaged ? damagePhoto : null,
      );
      setDifferenceOpen(false);
    } catch (err: unknown) {
      setError(errorText(err, "receiving.couldNotConfirm"));
    } finally {
      setBusy(null);
    }
  };

  const toggleDamaged = (next: boolean) => {
    setDamaged(next);
    // A photo only belongs to damaged units; unticking lets go of it.
    if (!next && damagePhoto) onRemoveDamagePhoto();
  };

  return (
    /*
      Owner ruling 2026-09-10: "instead of squares, I'd like to see lines,
      taking full width, showing picture, name, purchase price, [confirm
      exact / report difference]".

      Measured on the real tablet BEFORE this change: each line was a 366px
      wide card (of 960 available) and 247px tall, so a 107-line delivery was
      28,888px of scrolling — FIFTY-TWO screens to receive one delivery, with
      45% of the tablet's width sitting empty beside it. As a row the same
      delivery is a fraction of that, and the eye runs down one column of
      photos instead of hunting through stacked boxes.

      The photo leads because a florist matches what is in their hands against
      a picture faster than against a name.
    */
    <article
      className={`sf-delivery-row is-${line.status.toLowerCase()}${highlighted ? " delivery-line-found" : ""}`}
      aria-current={highlighted ? "true" : undefined}
    >
      <div className="sf-delivery-row__main">
        <ShopFloorDeliveryLinePhoto
          item={line.item}
          saving={photoSaving}
          error={photoError}
          onOpenCamera={onOpenCamera}
          onClearError={onClearPhotoError}
        />

        <span className="sf-delivery-row__copy">
          <strong className="sf-delivery-row__name">{line.item.name}</strong>
          {/* Owner, 2026-10-01: no zone, no prices, no formulas on this row —
              the florist checks WHAT arrived and HOW MANY (beside Confirm).
              Ruled knowingly for the D-67 case too: one item on two lines
              (one per zone) shows two rows that differ only in quantity. The
              only thing left under the name is the missing-barcode link. */}
          {pending && !line.item.barcode ? (
            <span className="muted sf-delivery-row__meta">
              <CaptureLineBarcode
                lineId={line.id}
                itemName={line.item.name}
                disabled={busy !== null}
                suggestedCode={unmatchedCode ?? undefined}
                onCapture={onCaptureBarcode}
                compact
              />
            </span>
          ) : null}
        </span>

        {/* Owner, 2026-10-01: beside Confirm the florist needs HOW MANY should
            arrive — that is what "Confirm exact" confirms. Purchase prices and
            billed formulas were noise at the door and left this screen; the
            money lives on the CRM delivery page. Labelled "expected" because a
            confirmed-short line shows a different "Received" number below. */}
        <span className="sf-delivery-row__qty">
          <strong>
            {qty(line.expectedQty)}{" "}
            {uomLabel(line.item.defaultUom, Number(line.expectedQty))}
          </strong>
          <span className="muted">{t("receiving.expectedCaption")}</span>
        </span>

        <span className="sf-delivery-row__actions">
          {/* G-130: the same square as the check, for the sticker — on every
              row, pending or confirmed, because goods get labelled while they
              are unpacked, whichever comes first. */}
          <button
            type="button"
            className="button plain sf-delivery-row__print"
            disabled={printLocked || busy !== null}
            aria-busy={printBusy}
            aria-label={printBusy ? t("printer.printing") : t("itemSticker.printBarcode")}
            title={t("itemSticker.printBarcode")}
            onClick={onPrint}
          >
            <ShopFloorIcon name="print" size={24} />
          </button>
          {pending ? (
            <>
              {/* Owner, 2026-10-01: a compact green check instead of the
                  worded "Confirm exact" — the words took most of a tablet
                  row and squeezed the product name. The full name stays as
                  the accessible label and the tooltip. While the difference
                  form is open it is off until "Close": a florist who typed
                  a missing count must press Save, not the check. */}
              <button
                type="button"
                className="button sf-delivery-row__done"
                disabled={busy !== null || differenceOpen}
                aria-busy={busy === "exact"}
                aria-label={busy === "exact" ? t("receiving.confirming") : t("receiving.confirmExact")}
                title={t("receiving.confirmExact")}
                onClick={() => void submitExact()}
              >
                <ShopFloorIcon name="check" size={24} />
              </button>
              <button
                type="button"
                className="button tinted"
                disabled={busy !== null}
                onClick={() => setDifferenceOpen((open) => !open)}
              >
                {differenceOpen ? t("common.close") : t("receiving.reportDifference")}
              </button>
            </>
          ) : (
            <span className={status.className}>{status.label}</span>
          )}
        </span>
      </div>

      {printWords?.notice || printWords?.error || printFallback ? (
        <div className="sf-delivery-row__print-result">
          {printWords?.notice ? <div className="alert success">{printWords.notice}</div> : null}
          {printWords?.error ? <div className="alert error">{printWords.error}</div> : null}
          {printFallback ? (
            <>
              <ShopFloorItemBarcodePrint itemName={line.item.name} barcode={printFallback} />
              <button type="button" className="button plain" onClick={onClosePrintFallback}>
                {t("common.close")}
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Everything below the row is the exception, not the norm: a confirmed
          line's outcome, an error, the barcode capture, the difference form. A
          pending line stays exactly one row tall. */}
      {line.status !== "PENDING" ? (
        <div className="shop-floor-delivery-line__result">
          <strong>
            {t("receiving.receivedQty", {
              qty: qty(line.receivedQty),
              unit: uomLabel(line.item.defaultUom, Number(line.receivedQty)),
            })}
          </strong>
          {positive(line.shortageQty) ? (
            <span>{t("receiving.shortage", { qty: qty(line.shortageQty) })}</span>
          ) : null}
          {positive(line.overageQty) ? (
            <span>{t("receiving.overage", { qty: qty(line.overageQty) })}</span>
          ) : null}
          {/* G-121 S2: a note — these are in stock like the rest. */}
          {positive(line.damagedQty) ? (
            <span>{t("receiving.damagedOnArrival", { qty: qty(line.damagedQty) })}</span>
          ) : null}
          {line.confirmedBy ? (
            <span>{t("receiving.confirmedBy", { who: line.confirmedBy.fullName })}</span>
          ) : null}
          {line.notes ? <span>{t("receiving.notesPrefix", { notes: line.notes })}</span> : null}
          {line.batch?.damagePhotoMovementId ? (
            <DamagePhotoThumb movementId={line.batch.damagePhotoMovementId} />
          ) : null}
        </div>
      ) : null}

      {error ? <div className="alert error">{error}</div> : null}
      {photoError ? <div className="alert error">{photoError}</div> : null}

      {pending ? (
        <>
          {differenceOpen ? (
            <div className="shop-floor-delivery-difference">
              <div className="field">
                <label htmlFor={`difference-${line.id}`}>
                  {damaged ? t("receiving.damagedCount") : t("receiving.missingQty")}
                </label>
                <input
                  id={`difference-${line.id}`}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max={line.expectedQty}
                  step="0.001"
                  value={differenceQty}
                  onChange={(event) => setDifferenceQty(event.target.value)}
                  disabled={busy !== null}
                />
                <label className="delivery-damaged-toggle">
                  <input
                    type="checkbox"
                    checked={damaged}
                    onChange={(event) => toggleDamaged(event.target.checked)}
                    disabled={busy !== null}
                  />
                  <span>{t("receiving.damagedTick")}</span>
                </label>
                {damaged ? (
                  <>
                    <small className="muted">{t("receiving.damagedHint")}</small>
                    <div className="shop-floor-delivery-damage-photo">
                      {damagePhoto ? (
                        <>
                          <LocalPhotoThumb file={damagePhoto} alt={t("receiving.damagePhoto")} />
                          <button
                            type="button"
                            className="button plain"
                            disabled={busy !== null}
                            onClick={onRemoveDamagePhoto}
                          >
                            {t("receiving.damagePhotoRemove")}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="button tinted"
                          disabled={busy !== null}
                          onClick={onOpenDamageCamera}
                        >
                          <ShopFloorIcon name="camera" size={18} />
                          {t("receiving.damagePhoto")}
                        </button>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
              <div className="field">
                <label htmlFor={`delivery-notes-${line.id}`}>{t("receiving.notesOptional")}</label>
                <input
                  id={`delivery-notes-${line.id}`}
                  type="text"
                  maxLength={500}
                  value={notes}
                  placeholder={t("receiving.notesPlaceholder")}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={busy !== null}
                />
              </div>
              <button
                type="button"
                className="button"
                disabled={busy !== null}
                onClick={() => void submitDifference()}
              >
                {busy === "difference" ? t("belowFloor.saving") : t("receiving.saveDifference")}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </article>
  );
}

/** A photo still on the tablet, shown from an object URL that is let go with it. */
function LocalPhotoThumb({ file, alt }: { file: File; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url ? <img src={url} alt={alt} /> : null;
}

/**
 * G-121 S2: the damage photo a confirmed line stored on its receive movement,
 * read through the floor token the way `ShopFloorBouquetThumb` reads a
 * product photo. Nothing is drawn until it arrives, and nothing at all when
 * the server has no photo for it.
 */
function DamagePhotoThumb({ movementId }: { movementId: string }) {
  const { t } = useT();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setUrl(null);
    void shopModeApi.floorWastePhotoBlobUrl(movementId).then((blobUrl) => {
      if (cancelled) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }
      objectUrl = blobUrl;
      setUrl(blobUrl);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [movementId]);

  if (!url) return null;
  return (
    <span className="shop-floor-delivery-damage-photo">
      <img src={url} alt={t("receiving.damagePhoto")} loading="lazy" decoding="async" />
    </span>
  );
}
